import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { createEffectFactory } from '@recruitee/ngrx';
import type { OfferFull } from '@recruitee/offers-types';
import { OfferStatus } from '@recruitee/offers-types';

import { OfferScope, StatusChangeType } from '../../types';
import { JobsFullActions } from './jobs-full.store';
import { JobsHttpService } from './jobs-http.service';

@Injectable({
  providedIn: 'root',
})
export class JobsFullEffects {
  public effects = createEffectFactory(JobsFullActions, this.actions$);

  public fetchItem$ = this.effects(JobsFullActions.fetchOne, ({ payload }) =>
    this.jobsHttpService
      .fetchItem$(payload)
      .pipe(map(resp => ({ id: payload.id, offer: resp.offer }))),
  );

  public create$ = this.effects(JobsFullActions.create, ({ payload }) =>
    this.jobsHttpService.create$({ offer: payload.offer }).pipe(
      switchMap(response => [JobsFullActions.createSuccess({ payload: { data: response.offer } })]),
      catchError(error => [JobsFullActions.createFailed({ payload: error })]),
    ),
  );

  public createFromRequisition$ = this.effects(
    JobsFullActions.createFromRequisition,
    ({ payload }) =>
      this.jobsHttpService.createFromRequisition$(payload.requisitionId).pipe(
        switchMap(response => [
          JobsFullActions.createFromRequisitionSuccess({ payload: { data: response.offer } }),
        ]),
        catchError(error => [JobsFullActions.createFromRequisitionFailed({ payload: error })]),
      ),
  );

  public update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.update),
      switchMap(({ payload }) =>
        this.jobsHttpService.update$({ offer: payload.offer }).pipe(
          switchMap(response => [
            JobsFullActions.updateSuccess({
              payload: { id: response.offer.id, data: response.offer },
            }),
          ]),
          catchError(error =>
            of(JobsFullActions.updateFailed({ payload: { id: payload.offer.id, error } })),
          ),
        ),
      ),
    ),
  );

  public follow$ = this.effects(JobsFullActions.follow, ({ payload }) =>
    this.jobsHttpService.follow(payload.id).pipe(
      switchMap(response => [
        JobsFullActions.followSuccess({ payload: { job: response.offer, scope: payload.scope } }),
      ]),
      catchError(error => of(JobsFullActions.followFailed({ payload: { error, id: payload.id } }))),
    ),
  );

  public unfollow$ = this.effects(JobsFullActions.unfollow, ({ payload }) =>
    this.jobsHttpService.unfollow(payload.id).pipe(
      switchMap(response => [
        JobsFullActions.unfollowSuccess({ payload: { job: response.offer, scope: payload.scope } }),
      ]),
      catchError(error =>
        of(JobsFullActions.unfollowFailed({ payload: { error, id: payload.id } })),
      ),
    ),
  );

  public duplicate$ = this.effects(JobsFullActions.duplicate, ({ payload }) =>
    this.jobsHttpService.duplicate$(payload.id).pipe(
      switchMap(response => [
        JobsFullActions.duplicateSuccess({ payload: { offer: response.offer } }),
      ]),
      catchError(error => [
        JobsFullActions.duplicateFailed({ payload: { id: payload.id, error } }),
      ]),
    ),
  );

  // eslint-disable-next-line max-lines-per-function
  public changeStatus$ = this.effects(JobsFullActions.changeStatus, ({ payload }) => {
    let status$: Observable<{ offer: OfferFull }>;
    const targetScope =
      payload.status === StatusChangeType.Archive ? OfferScope.Archived : OfferScope.Active;
    const isChangingScope =
      payload.currentStatus === OfferStatus.Archived || payload.status === StatusChangeType.Archive;

    switch (payload.status) {
      case StatusChangeType.Close:
        status$ = this.jobsHttpService.close(payload.id);
        break;
      case StatusChangeType.Archive:
        status$ = this.jobsHttpService.archive(payload.id);
        break;
      case StatusChangeType.Unarchive:
        status$ = this.jobsHttpService.unarchive(payload.id);
        break;
      case StatusChangeType.Draft:
        status$ = this.jobsHttpService.draft(payload.id);
        break;
      case StatusChangeType.Publish:
        status$ = this.jobsHttpService.publish(payload.id);
        break;
      case StatusChangeType.Unpublish:
        status$ = this.jobsHttpService.unpublish(payload.id);
        break;
      default:
        throw new Error(`Unknown status: ${payload.status}`);
    }

    return status$.pipe(
      switchMap(response => [
        JobsFullActions.changeStatusSuccess({
          payload: { offer: response.offer, targetScope, isChangingScope },
        }),
      ]),
      catchError(error => [
        JobsFullActions.changeStatusFailed({ payload: { error, id: payload.id } }),
      ]),
    );
  });

  public preview$ = this.effects(JobsFullActions.preview, ({ payload: { offer } }) =>
    this.jobsHttpService.preview$({ data: offer }).pipe(
      switchMap(({ previewUrl }) => [JobsFullActions.previewSuccess({ payload: { previewUrl } })]),
      catchError(error => [JobsFullActions.previewFailed({ payload: { error } })]),
    ),
  );

  public updateSocialMedia$ = this.effects(JobsFullActions.updateSocialMedia, ({ payload }) =>
    this.jobsHttpService.updateSocialMedia$(payload.id, payload).pipe(
      switchMap(response => [
        JobsFullActions.updateSocialMediaSuccess({ payload: { offer: response.offer } }),
      ]),
      catchError(error => [
        JobsFullActions.updateSocialMediaFailed({ payload: { id: payload.id, error } }),
      ]),
    ),
  );

  public updateSocialMediaImage$ = this.effects(
    JobsFullActions.updateSocialMediaImage,
    ({ payload }) =>
      this.jobsHttpService.updateSocialMediaImage$(payload.id, payload.file).pipe(
        switchMap(response => [
          JobsFullActions.updateSocialMediaImageSuccess({ payload: response }),
        ]),
        catchError(error => [
          JobsFullActions.updateSocialMediaImageFailed({ payload: { id: payload.id, error } }),
        ]),
      ),
  );

  public uploadCoverImage$ = this.effects(JobsFullActions.uploadCoverImage, ({ payload }) =>
    this.jobsHttpService.uploadCoverImage$(payload.id, payload.file).pipe(
      switchMap(response => [
        JobsFullActions.uploadCoverImageSuccess({ payload: { url: response.url } }),
      ]),
      catchError(error => [
        JobsFullActions.uploadCoverImageFailed({ payload: { id: payload.id, error } }),
      ]),
    ),
  );

  public updateSlug$ = this.effects(JobsFullActions.updateSlug, ({ payload }) =>
    this.jobsHttpService.updateSlug$(payload.id, payload.slug).pipe(
      switchMap(response => [
        JobsFullActions.updateSlugSuccess({ payload: { id: payload.id, offer: response.offer } }),
      ]),
      catchError(error => [
        JobsFullActions.updateSlugFailed({ payload: { id: payload.id, error } }),
      ]),
    ),
  );

  constructor(
    private actions$: Actions,
    private jobsHttpService: JobsHttpService,
  ) {}
}
